import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { TOP_BAR_STICKY } from '@/components/StickyBar';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { toastSuccess, toastError, toastInfo } from '@/lib/toast';
import { hasPolicyRichContent, PolicyRichContent, getPolicyDownloadHref } from '@/components/PolicyDocument';
import {
  MapPin,
  FolderTree,
  DollarSign,
  FileText,
  Upload,
  Trash2,
  Ban,
  RotateCcw,
  LogIn,
  LogOut,
  ArrowLeft,
  Loader2,
  Search,
  Download,
  CheckCircle,
  Shield,
  Eye,
  EyeOff,
  RefreshCw,
  History,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Users,
  Zap,
  Plus,
  Infinity,
  Mail,
  Brain,
  Sparkles,
  TrendingUp,
  ClipboardList,
  Ruler,
  ArrowLeftRight,
  Inbox
} from 'lucide-react';
import api from '@/lib/api';
// F13 (2026-09-08): every stored field is a column - see lib/masterColumns.js.
import { deriveMasterColumns } from '@/lib/masterColumns';
import { formatDate } from '@/lib/utils';
import { getStatusLabel } from '@/lib/statusLabels';

// Sidebar/mobile-strip nav items. `adminOnly` entries are filtered out for
// non-admin viewers.
const NAV_ITEMS = [
  { value: 'locations', label: 'Locations', icon: MapPin },
  { value: 'categories', label: 'Categories', icon: FolderTree },
  { value: 'uoms', label: 'UoM', icon: Ruler },
  { value: 'currencies', label: 'Currencies', icon: DollarSign },
  { value: 'exchange-rates', label: 'Exchange Rates', icon: ArrowLeftRight, adminOnly: true },
  { value: 'policy', label: 'Policy', icon: FileText },
  { value: 'risk', label: 'Risk Framework', icon: AlertTriangle },
  { value: 'credits', label: 'User Credits', icon: Users, adminOnly: true },
  { value: 'invites', label: 'Invites', icon: Mail, adminOnly: true },
  { value: 'learning', label: 'Learning', icon: Brain, adminOnly: true },
  { value: 'user-activity', label: 'Users', icon: TrendingUp, adminOnly: true },
  { value: 'all-requests', label: 'All Requests', icon: ClipboardList, adminOnly: true },
  // F1 / C1b-1b (B1). adminOnly, like every other operational tab -
  // the section's own isAdmin guard is the second line of defence and
  // both are needed.
  { value: 'master-data-requests', label: 'Data Requests', icon: Inbox, adminOnly: true },
];

// Measures a DOM node's rendered height via ResizeObserver. Used to size
// table scroll containers against the actual pinned zone above them
// (page header + section header) instead of a guessed pixel value.
const useMeasuredHeight = () => {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const updateHeight = () => setHeight(el.offsetHeight);
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, height];
};

// Sticky section header - pins flush below the page's sticky two-row header.
// `top` is the page header's measured height (px number), passed down from
// MasterDataManagement so it stays correct if that header's content wraps.
// `headerRef`, if given, lets callers measure this header's own rendered
// height (e.g. to size a table scroll area below it).
const SectionHeader = ({ top, headerRef, children }) => (
  <div ref={headerRef} className="sticky z-20 bg-white border-b border-zinc-200 -mx-6 px-6 py-4 space-y-3" style={{ top }}>
    {children}
  </div>
);

// Scrollable wrapper shared by every master-data table. Caps the table's
// height to whatever viewport space remains below the pinned page header +
// section header (`offset`, in px) so long tables (e.g. 120-row Categories)
// scroll internally instead of carrying their column headers off-screen.
// Short tables never hit the cap, so no scrollbar appears for them.
const TableScrollArea = ({ offset, children }) => (
  <div className="border border-zinc-200 rounded-lg overflow-hidden">
    <div
      className="overflow-auto"
      style={{ maxHeight: offset ? `calc(100vh - ${offset}px)` : undefined }}
    >
      {children}
    </div>
  </div>
);

// Admin Login Modal Component
const AdminLoginModal = ({ isOpen, onClose, onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.adminLogin(username, password);
      localStorage.setItem('admin_token', response.data.token);
      onLogin(true);
      onClose();
      toastSuccess('Logged in as admin');
    } catch (err) {
      console.debug('Login failed:', err.message);
      toastError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0055FF]" />
            Admin Login
          </DialogTitle>
          <DialogDescription>
            Enter admin credentials to manage master data
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <label className="text-sm font-medium text-zinc-700">Username</label>
            <Input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              className="mt-1"
              data-testid="admin-username-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700">Password</label>
            <div className="relative mt-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="pr-10"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading || !username || !password}
            className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LogIn className="w-4 h-4 mr-2" />}
            Login
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Data Table Component with Upload
const MasterDataTable = ({
  title,
  description,
  icon: Icon,
  data,
  columns,
  isAdmin,
  onUpload,
  onDelete,
  onActivate,
  onRefresh,
  loading,
  acceptedFileTypes = '.csv',
  stickyTop
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  // F15: the backend's change summary from the LAST upload in this
  // session. A toast is the wrong surface for it - it disappears before
  // an admin can read a rename list, and the rebind case is exactly the
  // one worth reading twice. Held until the next upload or a reload.
  const [changeSummary, setChangeSummary] = useState(null);
  const [sectionHeaderRef, sectionHeaderHeight] = useMeasuredHeight();
  // space-y-4 below adds a 16px gap between the section header and the table.
  const tableAreaOffset = stickyTop + sectionHeaderHeight + 16;

  const filteredData = data.filter(item => 
    Object.values(item).some(val => 
      String(val).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await onUpload(file);
      // B2b (2026-07-30): surface the backend's own message, which now
      // reports how many existing rows were deactivated (never deleted)
      // by this upload - falls back to a generic message for endpoints
      // that don't return one (e.g. policy upload reuses this handler).
      toastSuccess(response?.data?.message || `${title} uploaded successfully`);
      // F15: the detail behind that one-liner. Absent for endpoints that
      // do not return one (policy upload reuses this handler), in which
      // case the panel simply does not render.
      setChangeSummary(response?.data?.summary || null);
      onRefresh();
    } catch (err) {
      toastError(err.response?.data?.detail || `Failed to upload ${title}`);
      // A failed upload must not leave the previous run's summary on
      // screen looking like this one's result.
      setChangeSummary(null);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader top={stickyTop} headerRef={sectionHeaderRef}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#0055FF]/10 rounded-lg">
              <Icon className="w-5 h-5 text-[#0055FF]" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900">{title}</h3>
              <p className="text-sm text-zinc-500">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              className="border-zinc-200"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {isAdmin && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept={acceptedFileTypes}
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#0055FF] hover:bg-[#0044CC] rounded-md transition-colors">
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload CSV
                </div>
              </label>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-50 border-zinc-200"
          />
        </div>
      </SectionHeader>

      {/* F15: WHAT THE LAST UPLOAD ACTUALLY CHANGED.
          Rendered as a panel rather than only a toast: a toast vanishes
          before an admin can read a rename list, and the REBIND case is
          the one worth reading twice. It REPORTS ONLY - there is no
          confirmation step and no block, because a rename and a code
          redefinition are indistinguishable mechanically and legitimate
          renames must not be gated. */}
      {changeSummary && (
        <div
          className="border border-blue-200 bg-blue-50 rounded-lg p-4 text-sm"
          data-testid="upload-change-summary"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-blue-900">
              What this upload changed
            </span>
            <button
              type="button"
              onClick={() => setChangeSummary(null)}
              className="text-xs text-blue-700 underline"
              data-testid="upload-change-summary-dismiss"
            >
              dismiss
            </button>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-blue-900/90 mb-2">
            <span data-testid="summary-count-added">
              Added: {changeSummary.added?.length ?? 0}
            </span>
            <span data-testid="summary-count-renamed">
              Renamed: {changeSummary.renamed?.length ?? 0}
            </span>
            <span data-testid="summary-count-reactivated">
              Reactivated: {changeSummary.reactivated?.length ?? 0}
            </span>
            <span data-testid="summary-count-deactivated">
              Deactivated: {changeSummary.deactivated_by_absence?.length ?? 0}
            </span>
            <span data-testid="summary-count-content">
              Content changed: {changeSummary.content_changed ?? 0}
            </span>
            <span data-testid="summary-count-frozen">
              Left retired: {changeSummary.frozen_admin_retired?.length ?? 0}
            </span>
            <span className="text-blue-900/60" data-testid="summary-count-unchanged">
              Unchanged: {changeSummary.unchanged ?? 0}
            </span>
          </div>

          {changeSummary.reactivated?.length > 0 && (
            /* THE REBIND CASE, given its own block and its own wording.
               Documents store only the CODE, so a dormant row coming back
               under a different name silently changes what every
               historical document referencing it means. */
            <div className="mb-2" data-testid="summary-reactivated-detail">
              <div className="font-medium text-blue-900">
                Reactivated from a previous upload - check the meaning did not change:
              </div>
              <ul className="list-disc ml-5">
                {changeSummary.reactivated.map((r) => (
                  <li key={`react-${r.code}`}>
                    <span className="font-mono">{r.code}</span>
                    {' — was '}
                    <span className="italic">{r.previous_name || '(no name)'}</span>
                    {' → now '}
                    <span className="italic">{r.new_name || '(no name)'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {changeSummary.renamed?.length > 0 && (
            <div className="mb-2" data-testid="summary-renamed-detail">
              <div className="font-medium text-blue-900">Renamed:</div>
              <ul className="list-disc ml-5">
                {changeSummary.renamed.map((r) => (
                  <li key={`rename-${r.code}`}>
                    <span className="font-mono">{r.code}</span>
                    {' — '}
                    <span className="italic">{r.from || '(no name)'}</span>
                    {' → '}
                    <span className="italic">{r.to || '(no name)'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {changeSummary.deactivated_by_absence?.length > 0 && (
            <div className="mb-2" data-testid="summary-deactivated-detail">
              <div className="font-medium text-blue-900">
                Deactivated (absent from this file, never deleted):
              </div>
              <div className="font-mono">
                {changeSummary.deactivated_by_absence.join(', ')}
              </div>
            </div>
          )}

          {changeSummary.frozen_admin_retired?.length > 0 && (
            <div data-testid="summary-frozen-detail">
              <div className="font-medium text-blue-900">
                Left retired - present in the file but not reactivated, and their
                definitions were not rewritten:
              </div>
              <div className="font-mono">
                {changeSummary.frozen_admin_retired.join(', ')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <TableScrollArea offset={tableAreaOffset}>
        <Table containerClassName="relative w-full overflow-visible" className="min-w-full w-max">
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key} className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">
                  {col.label}
                </TableHead>
              ))}
              {isAdmin && <TableHead className="sticky top-0 z-10 bg-zinc-50 w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (isAdmin ? 1 : 0)} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (isAdmin ? 1 : 0)} className="text-center py-8 text-zinc-500">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item, idx) => {
                  // F14: an inactive row is VISIBLE and MARKED. It used to
                  // be invisible in practice because nothing could produce
                  // one - the delete button destroyed the row instead. An
                  // admin cannot reinstate what the list hides.
                  const isInactive = item.is_active === false;
                  return (
                  <TableRow
                    key={item.id || idx}
                    className={isInactive ? 'bg-amber-50/60' : 'hover:bg-zinc-50'}
                    data-testid={isInactive ? `inactive-row-${item.id}` : undefined}
                  >
                    {columns.map((col, colIdx) => (
                      <TableCell
                        key={col.key}
                        className={`${col.derived ? 'text-xs align-top whitespace-normal min-w-[8rem] max-w-md' : 'text-sm'} ${isInactive ? 'text-zinc-500' : ''}`}
                      >
                        {colIdx === 0 && isInactive && (
                          <span
                            className="mr-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide
                                       border border-amber-300 bg-amber-100 text-amber-900 rounded-sm"
                            data-testid={`inactive-badge-${item.id}`}
                          >
                            Inactive
                          </span>
                        )}
                        {col.render ? col.render(item[col.key], item) : item[col.key]}
                      </TableCell>
                    ))}
                    {isAdmin && (
                      <TableCell>
                        {isInactive ? (
                          onActivate && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onActivate(item.id)}
                              className="text-amber-700 hover:text-green-600"
                              data-testid={`activate-${item.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Activate
                            </Button>
                          )
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDelete(item.id)}
                            className="text-zinc-400 hover:text-amber-600"
                            data-testid={`deactivate-${item.id}`}
                          >
                            <Ban className="w-4 h-4 mr-1" />
                            Deactivate
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  );
                })
              )}
          </TableBody>
        </Table>
      </TableScrollArea>

      <div className="text-sm text-zinc-500">
        Showing {filteredData.length} of {data.length} records
      </div>
    </div>
  );
};

// Exchange Rates Section (B2-A, stage 1 of 3 - Admin Only). Unlike
// currencies/uoms/categories (visible to every logged-in user for wizard
// dropdowns, admin token only gates a few extra columns), this master has
// no non-admin consumer at all yet - same admin-only-end-to-end shape as
// UserCreditsSection/LearningStatsSection below, self-gating its own
// fetch and rendering a placeholder for non-admins, rather than the
// always-fetch-on-mount pattern the four browse-able masters use. Reuses
// the shared MasterDataTable for the actual table/upload UI once admin
// status is confirmed - no reason to duplicate that.
const ExchangeRatesSection = ({ isAdmin, stickyTop }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRates = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.getMasterExchangeRates();
      setRates(response.data);
    } catch (err) {
      console.error('Error fetching exchange rates:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const exchangeRateColumns = [
    { key: 'currency_code', label: 'Currency' },
    { key: 'rate_to_usd', label: 'Rate to USD' },
    { key: 'effective_date', label: 'Effective Date' },
  ];

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <ArrowLeftRight className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-500">Login as admin to manage exchange rates</p>
      </div>
    );
  }

  return (
    <MasterDataTable
      title="Exchange Rates"
      description="USD-pivot rates (rate_to_usd) - not yet consumed anywhere; inert stage 1 of 3"
      icon={ArrowLeftRight}
      data={rates}
      columns={exchangeRateColumns}
      isAdmin={isAdmin}
      onUpload={api.uploadMasterExchangeRates}
      onDelete={() => toastInfo('Exchange rate deletion not supported')}
      onRefresh={fetchRates}
      loading={loading}
      stickyTop={stickyTop}
    />
  );
};

// Policy Section Component
const PolicySection = ({ isAdmin, onRefresh, policyType = 'procurement', title = 'Procurement Policy', description = 'Current active policy document', stickyTop }) => {
  const [policy, setPolicy] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);
  const PREVIEW_LENGTH = 3000; // Characters to show in preview mode

  const fetchPolicy = useCallback(async () => {
    try {
      const [policyRes, historyRes] = await Promise.all([
        api.getMasterPolicy(policyType),
        api.getPolicyHistory(policyType)
      ]);
      setPolicy(policyRes.data);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Error fetching policy:', err);
    } finally {
      setLoading(false);
    }
  }, [policyType]);

  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      await api.uploadMasterPolicy(file, policyType);
      toastSuccess('Policy uploaded successfully');
      fetchPolicy();
    } catch (err) {
      toastError(err.response?.data?.detail || 'Failed to upload policy');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader top={stickyTop}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#0055FF]/10 rounded-lg">
              <FileText className="w-5 h-5 text-[#0055FF]" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900">{title}</h3>
              <p className="text-sm text-zinc-500">{description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="border-zinc-200"
            >
              <History className="w-4 h-4 mr-2" />
              {showHistory ? 'Hide History' : 'Show History'}
            </Button>
            {isAdmin && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".docx,.pdf,.txt"
                  onChange={handleUpload}
                  className="hidden"
                />
                <div className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#0055FF] hover:bg-[#0044CC] rounded-md transition-colors">
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4" />
                  )}
                  Upload Policy
                </div>
              </label>
            )}
          </div>
        </div>
      </SectionHeader>

      {/* Current Policy */}
      {policy && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-sm font-medium text-zinc-900">{policy.filename}</span>
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
              {policy.content && (
                <span className="text-xs text-zinc-400">
                  ({policy.content.length.toLocaleString()} characters)
                </span>
              )}
            </div>
            {getPolicyDownloadHref(policy) && (
              <a
                href={getPolicyDownloadHref(policy)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#0055FF] hover:underline flex items-center gap-1"
              >
                <Download className="w-4 h-4" />
                Download Original
              </a>
            )}
          </div>
          {hasPolicyRichContent(policy) ? (
            <PolicyRichContent policy={policy} heightClass="h-[600px]" />
          ) : (
            <>
              <div className={`text-sm text-zinc-600 ${showFullContent ? 'max-h-[600px]' : 'max-h-[300px]'} overflow-auto whitespace-pre-wrap bg-white border border-zinc-200 rounded p-3`}>
                {showFullContent
                  ? policy.content
                  : policy.content?.slice(0, PREVIEW_LENGTH)}
                {!showFullContent && policy.content?.length > PREVIEW_LENGTH && '...'}
              </div>
              {policy.content?.length > PREVIEW_LENGTH && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setShowFullContent(!showFullContent)}
                  className="mt-2 text-[#0055FF] p-0 h-auto"
                >
                  {showFullContent ? 'Show Less' : `Show Full Content (${Math.ceil(policy.content.length / 1000)}k chars)`}
                </Button>
              )}
            </>
          )}
        </div>
      )}

      {/* Policy History */}
      {showHistory && history.length > 0 && (
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200">
            <h4 className="text-sm font-medium text-zinc-700">Policy History</h4>
          </div>
          <div className="max-h-[200px] overflow-auto">
            {history.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 last:border-0">
                <div>
                  <span className="text-sm font-medium text-zinc-900">{p.filename}</span>
                  <span className="text-xs text-zinc-500 ml-2">
                    {new Date(p.uploaded_at).toLocaleDateString()}
                  </span>
                  {p.is_active && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded ml-2">Current</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Risk Framework Section Component
const RiskFrameworkSection = ({ isAdmin, stickyTop }) => {
  const [riskDomains, setRiskDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [editedWeights, setEditedWeights] = useState({});
  const [savingDomainId, setSavingDomainId] = useState(null);
  const [thresholds, setThresholds] = useState(null);
  // Spend Thresholds (Ashok ruling, 2026-08-03, Change 5) - distinct from
  // `thresholds` above (risk SCORE bands, 0-10 scale, display-only here).
  // spendThresholds is the money-band config (low/medium/high/critical:
  // min/max/financial_review), admin-editable via PUT /risk/spend-
  // thresholds (existing wrapper, api.js:315/318 - had zero call sites
  // until this section).
  const [spendThresholds, setSpendThresholds] = useState(null);
  const [savingSpendThresholds, setSavingSpendThresholds] = useState(false);
  const [resettingSpendThresholds, setResettingSpendThresholds] = useState(false);
  const [spendThresholdsError, setSpendThresholdsError] = useState(null);

  const fetchRiskDomains = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getRiskDomains();
      setRiskDomains(response.data.risk_domains || []);
    } catch (err) {
      console.error('Error fetching risk domains:', err);
      toastError('Failed to load risk framework');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchThresholds = useCallback(async () => {
    try {
      const response = await api.getRiskThresholds();
      setThresholds(response.data.thresholds);
    } catch (err) {
      console.error('Error fetching risk thresholds:', err);
    }
  }, []);

  const fetchSpendThresholds = useCallback(async () => {
    try {
      const response = await api.getSpendThresholds();
      setSpendThresholds(response.data.thresholds);
    } catch (err) {
      console.error('Error fetching spend thresholds:', err);
    }
  }, []);

  useEffect(() => {
    fetchRiskDomains();
    fetchThresholds();
    fetchSpendThresholds();
  }, [fetchRiskDomains, fetchThresholds, fetchSpendThresholds]);

  // Spend Thresholds edit handlers. min/max are stored as numbers or
  // undefined (low has no min, critical has no max) - the input's own
  // empty-string state is normalized to undefined so the PUT payload
  // matches the {min?, max?, financial_review} shape the backend
  // validates (routes/risk_management.py's SpendThresholdBandInput).
  //
  // DEFECT 2 fix (2026-08-03, part 2 - the "stale error" half): a failed
  // save's error used to persist on screen indefinitely - editing a
  // field afterward never cleared it, so the message could describe a
  // payload from several edits ago while the checkboxes/inputs on
  // screen showed something else entirely (the exact "displayed state
  // and server state disagree" symptom Ashok observed). Clearing it
  // here means the error only ever describes the LAST payload actually
  // rejected, never a stale one contradicted by the user's own further
  // edits.
  const handleSpendThresholdFieldChange = (band, field, value) => {
    setSpendThresholdsError(null);
    setSpendThresholds(prev => ({
      ...prev,
      [band]: {
        ...prev[band],
        [field]: field === 'financial_review' ? value : (value === '' ? undefined : Number(value)),
      },
    }));
  };

  const handleSaveSpendThresholds = async () => {
    setSavingSpendThresholds(true);
    setSpendThresholdsError(null);
    try {
      await api.updateSpendThresholds({
        low: { max: spendThresholds.low?.max, financial_review: !!spendThresholds.low?.financial_review },
        medium: {
          min: spendThresholds.medium?.min, max: spendThresholds.medium?.max,
          financial_review: !!spendThresholds.medium?.financial_review,
        },
        high: {
          min: spendThresholds.high?.min, max: spendThresholds.high?.max,
          financial_review: !!spendThresholds.high?.financial_review,
        },
        critical: { min: spendThresholds.critical?.min, financial_review: !!spendThresholds.critical?.financial_review },
      });
      toastSuccess('Spend thresholds updated');
      await fetchSpendThresholds();
    } catch (err) {
      // Tiling/monotonicity violations (routes/risk_management.py's
      // validate_spend_thresholds_tiling_and_monotonicity) surface here,
      // naming the offending bands/values verbatim.
      setSpendThresholdsError(err.response?.data?.detail || 'Failed to update spend thresholds');
    } finally {
      setSavingSpendThresholds(false);
    }
  };

  // Reset affordance for the Spend Thresholds panel (Ashok ruling,
  // 2026-08-03). Before this there was no path back to defaults except
  // retyping every band. SCOPED TO THIS PANEL ONLY - calls POST
  // /risk/spend-thresholds/reset-defaults, which touches
  // db.spend_thresholds alone; it never calls the risk-domains reset and
  // never touches riskDomains/editedWeights state. The label names its
  // scope for the same reason the risk-domains button's does.
  //
  // CONFIRMATION REQUIRED: this discards organisational configuration -
  // an org may have deliberately set its high band to 40,000. A single
  // misclick must not silently wipe that, so the dialog states the exact
  // values that will be restored rather than a generic "are you sure".
  const handleResetSpendThresholds = async () => {
    const confirmed = window.confirm(
      'Reset spend thresholds to defaults?\n\n' +
      'This discards your organisation\'s current spend-band configuration and restores:\n' +
      '  Low: 0 - 10,000 (no financial review)\n' +
      '  Medium: 10,000 - 50,000 (no financial review)\n' +
      '  High: 50,000 - 250,000 (financial review required)\n' +
      '  Critical: 250,000 and above (financial review required)\n\n' +
      'Risk domains are not affected.'
    );
    if (!confirmed) return;
    setResettingSpendThresholds(true);
    setSpendThresholdsError(null);
    try {
      await api.resetSpendThresholdsToDefaults();
      toastSuccess('Spend thresholds reset to defaults');
      await fetchSpendThresholds();
    } catch (err) {
      setSpendThresholdsError(err.response?.data?.detail || 'Failed to reset spend thresholds');
    } finally {
      setResettingSpendThresholds(false);
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm('Reset all risk domains to default values? This will overwrite any customizations.')) return;
    try {
      await api.resetRiskDomainsToDefaults();
      toastSuccess('Risk domains reset to defaults');
      setEditedWeights({});
      fetchRiskDomains();
    } catch (err) {
      toastError('Failed to reset risk domains');
    }
  };

  const toggleDomain = (domainId) => {
    setExpandedDomain(expandedDomain === domainId ? null : domainId);
  };

  const handleWeightChange = (domainId, value) => {
    setEditedWeights(prev => ({ ...prev, [domainId]: value }));
  };

  const handleSaveWeight = async (domain) => {
    const newWeight = Number(editedWeights[domain.id] ?? domain.weight);
    if (Number.isNaN(newWeight) || newWeight < 0 || newWeight > 100) {
      toastError('Weight must be a number between 0 and 100');
      return;
    }

    setSavingDomainId(domain.id);
    try {
      await api.updateRiskDomain(domain.id, { ...domain, weight: newWeight });
      toastSuccess(`${domain.name} weight updated`);
      setEditedWeights(prev => {
        const next = { ...prev };
        delete next[domain.id];
        return next;
      });
      fetchRiskDomains();
    } catch (err) {
      toastError(err.response?.data?.detail || 'Failed to update weight');
    } finally {
      setSavingDomainId(null);
    }
  };

  const totalWeight = riskDomains.reduce((sum, d) => sum + (Number(d.weight) || 0), 0);
  const totalWeightValid = Math.abs(totalWeight - 100) <= 0.01;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055FF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader top={stickyTop}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900">Vendor Risk Scoring Framework</h3>
              <p className="text-sm text-zinc-500">8 risk domains with weighted factors for vendor assessment</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchRiskDomains}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {isAdmin && (
              // DEFECT 2 fix (2026-08-03, part 1 - the "scope" half):
              // relabeled from "Reset to Defaults" - that generic label
              // reasonably reads as "reset this whole screen", but the
              // handler (handleResetDefaults) only ever calls POST
              // /risk-domains/reset-defaults, which touches db.risk_domains
              // alone; it never touches db.spend_thresholds or this
              // component's spendThresholds state. Spend Thresholds now has
              // its OWN reset affordance (handleResetSpendThresholds,
              // 2026-08-03) - built as a separate, symmetric button, which
              // is exactly why this button's scope was never widened.
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetDefaults}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid="reset-risk-domains-btn"
              >
                Reset Risk Domains to Defaults
              </Button>
            )}
          </div>
        </div>
      </SectionHeader>

      {/* Total Weight Indicator */}
      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium ${
        totalWeightValid ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-red-50 border-red-200 text-red-700'
      }`}>
        {!totalWeightValid && <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
        Total weight: {totalWeight}% {!totalWeightValid && '— must sum to 100%'}
      </div>

      {/* Domain Cards */}
      <div className="space-y-3">
        {riskDomains.map((domain) => (
          <div key={domain.id} className="border border-zinc-200 rounded-lg overflow-hidden">
            {/* Domain Header */}
            <div className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors">
              <div
                className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                onClick={() => toggleDomain(domain.id)}
              >
                {isAdmin ? (
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <NumberInput
                      min="0"
                      max="100"
                      value={editedWeights[domain.id] ?? domain.weight}
                      onChange={(e) => handleWeightChange(domain.id, e.target.value)}
                      className="w-16 h-8 text-center text-sm"
                      data-testid={`domain-weight-input-${domain.id}`}
                    />
                    <span className="text-xs text-zinc-500">%</span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSaveWeight(domain)}
                      disabled={savingDomainId === domain.id || Number(editedWeights[domain.id] ?? domain.weight) === domain.weight}
                      className="h-8 px-2 text-xs"
                      data-testid={`domain-weight-save-${domain.id}`}
                    >
                      {savingDomainId === domain.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                ) : (
                  <span className="w-12 h-8 flex items-center justify-center bg-[#0055FF] text-white text-sm font-bold rounded flex-shrink-0">
                    {domain.weight}%
                  </span>
                )}
                <div className="text-left min-w-0">
                  <h4 className="font-medium text-zinc-900">{domain.name}</h4>
                  <p className="text-xs text-zinc-500">{domain.description}</p>
                </div>
              </div>
              <button onClick={() => toggleDomain(domain.id)} className="p-1 flex-shrink-0">
                {expandedDomain === domain.id ? (
                  <ChevronUp className="w-5 h-5 text-zinc-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-zinc-400" />
                )}
              </button>
            </div>

            {/* Domain Factors */}
            {expandedDomain === domain.id && (
              <div className="px-4 py-3 bg-white border-t border-zinc-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-zinc-500 border-b border-zinc-100">
                      <th className="pb-2 font-medium">Risk Factor</th>
                      <th className="pb-2 font-medium w-20">Weight</th>
                      <th className="pb-2 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domain.factors?.map((factor, idx) => (
                      <tr key={factor.id || idx} className="border-b border-zinc-50 last:border-0">
                        <td className="py-2 font-medium text-zinc-700">{factor.name}</td>
                        <td className="py-2">
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                            {factor.weight}%
                          </span>
                        </td>
                        <td className="py-2 text-zinc-500">{factor.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Applies To */}
                {domain.applies_to && domain.applies_to.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-zinc-100">
                    <span className="text-xs text-zinc-500">Applies to categories: </span>
                    <span className="text-xs text-zinc-700">{domain.applies_to.join(', ')}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Risk Thresholds Info - fetched live from /risk-thresholds, not hardcoded,
          so this can never drift out of sync with what risk_scoring_agent actually uses. */}
      {thresholds && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-amber-900 mb-2">Risk Score Thresholds (weighted total, 0-100 scale)</h4>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-green-100 rounded p-2 text-center">
              <div className="font-bold text-green-800">0 - {thresholds.low.max}</div>
              <div className="text-green-700">{thresholds.low.label}</div>
            </div>
            <div className="bg-yellow-100 rounded p-2 text-center">
              <div className="font-bold text-yellow-800">{thresholds.medium.min} - {thresholds.medium.max}</div>
              <div className="text-yellow-700">{thresholds.medium.label}</div>
            </div>
            <div className="bg-red-100 rounded p-2 text-center">
              {/* DEFECT 3 fix (2026-08-03): was a hardcoded "10", a
                  leftover from the pre-B3 0-10 scale (B3, 2026-07-30,
                  rescaled risk_score to 0-100 integer) - RISK_THRESHOLDS'
                  "high" band has no "max" key (open-ended above), so 100
                  (the true ceiling of the current scale) is the correct
                  literal here, not a value read off `thresholds`. */}
              <div className="font-bold text-red-800">{thresholds.high.min} - 100</div>
              <div className="text-red-700">{thresholds.high.label}</div>
            </div>
          </div>
        </div>
      )}

      {/* Spend Thresholds (Ashok ruling, 2026-08-03, Change 5) - the
          financial-review gate's bands, admin-editable. Money bands, not
          the risk-SCORE bands above - see this component's spendThresholds
          state comment. */}
      {spendThresholds && (
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
            <h4 className="font-semibold text-zinc-900">Spend Thresholds</h4>
            <p className="text-sm text-zinc-500">
              Bands must tile with no gaps or overlaps. Financial review must be monotonic - once required on a band, it stays required on every higher band.
            </p>
          </div>
          {spendThresholdsError && (
            <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-sm text-red-700" data-testid="spend-thresholds-error">
              {spendThresholdsError}
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 border-b border-zinc-100 bg-zinc-50">
                <th className="px-4 py-2 font-medium">Band</th>
                <th className="px-4 py-2 font-medium">Min (USD)</th>
                <th className="px-4 py-2 font-medium">Max (USD)</th>
                <th className="px-4 py-2 font-medium">Financial Review</th>
              </tr>
            </thead>
            <tbody>
              {['low', 'medium', 'high', 'critical'].map((band) => (
                <tr key={band} className="border-b border-zinc-50 last:border-0">
                  <td className="px-4 py-2 font-medium text-zinc-700 capitalize">{band}</td>
                  <td className="px-4 py-2">
                    {band === 'low' ? (
                      <span className="text-zinc-400 text-xs">0 (fixed)</span>
                    ) : (
                      <NumberInput
                        value={spendThresholds[band]?.min ?? ''}
                        onChange={(e) => handleSpendThresholdFieldChange(band, 'min', e.target.value)}
                        className="w-28 h-8 text-sm"
                        disabled={!isAdmin}
                        data-testid={`spend-threshold-${band}-min`}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {band === 'critical' ? (
                      <span className="text-zinc-400 text-xs">Unbounded</span>
                    ) : (
                      <NumberInput
                        value={spendThresholds[band]?.max ?? ''}
                        onChange={(e) => handleSpendThresholdFieldChange(band, 'max', e.target.value)}
                        className="w-28 h-8 text-sm"
                        disabled={!isAdmin}
                        data-testid={`spend-threshold-${band}-max`}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={!!spendThresholds[band]?.financial_review}
                      onChange={(e) => handleSpendThresholdFieldChange(band, 'financial_review', e.target.checked)}
                      disabled={!isAdmin}
                      data-testid={`spend-threshold-${band}-financial-review`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {isAdmin && (
            <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-200 flex justify-end gap-2">
              {/* Scoped to this panel - see handleResetSpendThresholds.
                  The label names its scope for the same reason "Reset
                  Risk Domains to Defaults" does: a bare "Reset to
                  Defaults" reads as "reset this whole screen". */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetSpendThresholds}
                disabled={resettingSpendThresholds}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid="reset-spend-thresholds-btn"
              >
                {resettingSpendThresholds ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null}
                Reset Spend Thresholds to Defaults
              </Button>
              <Button
                size="sm"
                onClick={handleSaveSpendThresholds}
                disabled={savingSpendThresholds}
                data-testid="save-spend-thresholds-btn"
              >
                {savingSpendThresholds ? <Loader2 className="w-3 h-3 animate-spin mr-2" /> : null}
                Save Spend Thresholds
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// User Credits Management Section (Admin Only)
// ============================================================================
// BETA INVITES (admin only)
// ============================================================================
// The invite link is returned by exactly one response and is never stored -
// the server keeps only a hash. The UI therefore has to SAY that, plainly,
// rather than presenting the link as something retrievable later. Losing it
// means minting a replacement, which revokes the old one.
const InvitesSection = ({ isAdmin, stickyTop }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // The one-shot link. Held in component state only - never persisted,
  // never re-fetched, cleared when the admin dismisses it.
  const [mintedLink, setMintedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  // Admin-initiated password reset shares this section and the same
  // one-shot display: a reset link is exactly as unrecoverable as an invite.
  const [resetEmail, setResetEmail] = useState('');
  const [resetting, setResetting] = useState(false);

  const fetchInvites = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.getInvites();
      setInvites(response.data.invites || []);
    } catch (err) {
      console.error('Error fetching invites:', err);
      toastError('Failed to fetch invites');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) {
      toastError('Email and name are both required');
      return;
    }
    setCreating(true);
    try {
      const response = await api.createInvite(email.trim(), name.trim());
      setMintedLink({ url: response.data.invite_url, email: response.data.email });
      setCopied(false);
      setEmail('');
      setName('');
      await fetchInvites();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toastError(typeof detail === 'string' ? detail : 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateReset = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toastError('An email is required');
      return;
    }
    setResetting(true);
    try {
      const response = await api.createPasswordReset(resetEmail.trim());
      setMintedLink({
        url: response.data.reset_url,
        email: response.data.email,
        kind: 'reset',
      });
      setCopied(false);
      setResetEmail('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toastError(typeof detail === 'string' ? detail : 'Failed to create reset link');
    } finally {
      setResetting(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(mintedLink.url);
      setCopied(true);
      toastSuccess('Invite link copied');
    } catch {
      toastError('Could not copy - select the link and copy it manually');
    }
  };

  const handleRevoke = async (userId, inviteEmail) => {
    try {
      await api.revokeInvite(userId);
      toastSuccess(`Invite for ${inviteEmail} revoked`);
      await fetchInvites();
    } catch (err) {
      toastError('Failed to revoke invite');
    }
  };

  if (!isAdmin) {
    return <p className="text-sm text-zinc-500">Admin access required.</p>;
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3" data-testid="invite-form">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-zinc-600 mb-1" htmlFor="invite-email">Email</label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com"
            data-testid="invite-email"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-zinc-600 mb-1" htmlFor="invite-name">Name</label>
          <Input
            id="invite-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Full name"
            data-testid="invite-name"
          />
        </div>
        <Button type="submit" disabled={creating} data-testid="invite-submit">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
          Create invite
        </Button>
      </form>

      {mintedLink && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-4"
          data-testid="minted-invite"
        >
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-sm font-medium text-amber-900" data-testid="minted-invite-warning">
              Copy this link now. It is shown once and cannot be retrieved later &mdash;
              only a hash is stored. If it is lost, create a new
              {mintedLink.kind === 'reset' ? ' reset link' : ' invite'}, which revokes this one.
            </p>
          </div>
          <p className="text-xs text-amber-800 mb-2" data-testid="minted-invite-meta">
            For {mintedLink.email}. Expires in {mintedLink.kind === 'reset' ? '24' : '72'} hours.
            {mintedLink.kind === 'reset' && ' Using it signs that account out everywhere else.'}
          </p>
          <code
            className="block w-full break-all rounded border border-amber-200 bg-white px-3 py-2 text-xs text-zinc-800"
            data-testid="minted-invite-url"
          >
            {mintedLink.url}
          </code>
          <div className="mt-3 flex gap-2">
            <Button type="button" size="sm" onClick={handleCopy} data-testid="minted-invite-copy">
              {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : null}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setMintedLink(null)}
              data-testid="minted-invite-dismiss"
            >
              Done
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
      ) : (
        <Table data-testid="invites-table">
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Invited</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>By</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-zinc-500">
                  No outstanding invites.
                </TableCell>
              </TableRow>
            ) : (
              invites.map((invite) => (
                <TableRow key={invite.user_id} data-testid={`invite-row-${invite.user_id}`}>
                  <TableCell>{invite.email}</TableCell>
                  <TableCell>{invite.name}</TableCell>
                  <TableCell>{formatDate(invite.created_at)}</TableCell>
                  <TableCell>{formatDate(invite.expires_at)}</TableCell>
                  <TableCell>{invite.created_by}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleRevoke(invite.user_id, invite.email)}
                      data-testid={`invite-revoke-${invite.user_id}`}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <div className="border-t border-zinc-200 pt-6">
        <h3 className="text-sm font-semibold text-zinc-900 mb-1">Password reset</h3>
        <p className="text-xs text-zinc-500 mb-3">
          For an account that already exists. The link is shown once, expires sooner than an
          invite, and signs that account out everywhere else when used.
        </p>
        <form onSubmit={handleCreateReset} className="flex flex-wrap items-end gap-3" data-testid="reset-form">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-zinc-600 mb-1" htmlFor="reset-email">Email</label>
            <Input
              id="reset-email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              placeholder="person@company.com"
              data-testid="reset-email"
            />
          </div>
          <Button type="submit" variant="outline" disabled={resetting} data-testid="reset-submit">
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create reset link'}
          </Button>
        </form>
      </div>
    </div>
  );
};

const UserCreditsSection = ({ isAdmin, stickyTop }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState(10);
  const [addMode, setAddMode] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [sectionHeaderRef, sectionHeaderHeight] = useMeasuredHeight();
  // space-y-6 below adds a 24px gap between the section header and the table.
  const tableAreaOffset = stickyTop + sectionHeaderHeight + 24;

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.getAdminUsers();
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      toastError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleUpdateCredits = async (email) => {
    setUpdating(true);
    try {
      await api.updateUserCredits(email, creditAmount, addMode);
      toastSuccess(`Credits ${addMode ? 'added to' : 'set for'} ${email}`);
      setEditingUser(null);
      setCreditAmount(10);
      fetchUsers();
    } catch (err) {
      toastError('Failed to update credits');
    } finally {
      setUpdating(false);
    }
  };

  const handleMakeOwner = async (email) => {
    if (!window.confirm(`Grant unlimited access to ${email}?`)) return;
    try {
      await api.makeUserOwner(email);
      toastSuccess(`${email} now has unlimited access`);
      fetchUsers();
    } catch (err) {
      toastError('Failed to update user');
    }
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-500">Login as admin to manage user credits</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader top={stickyTop} headerRef={sectionHeaderRef}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Zap className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900">User Credits</h3>
              <p className="text-sm text-zinc-500">Manage beta user usage limits</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchUsers}
            className="border-zinc-200"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-50 border-zinc-200"
          />
        </div>
      </SectionHeader>

      {/* Users Table */}
      <TableScrollArea offset={tableAreaOffset}>
        <Table containerClassName="relative w-full overflow-visible">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">User</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Email</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 text-center">Credits</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Last Login</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => {
                  const isOwnerUser = user.is_owner || user.usage_limit === -1 || user.user_role === 'owner';
                  return (
                  <TableRow key={user.user_id} className="hover:bg-zinc-50">
                    <TableCell className="font-medium text-zinc-900">
                      {user.name || 'Unknown'}
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3 text-zinc-400" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {editingUser === user.email ? (
                        <div className="flex items-center justify-center gap-2">
                          <select
                            value={addMode ? 'add' : 'set'}
                            onChange={(e) => setAddMode(e.target.value === 'add')}
                            className="text-xs border rounded px-1 py-1"
                          >
                            <option value="add">Add</option>
                            <option value="set">Set to</option>
                          </select>
                          <NumberInput
                            value={creditAmount}
                            onChange={(e) => setCreditAmount(parseInt(e.target.value) || 0)}
                            className="w-16 h-7 text-center text-sm"
                            min="0"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateCredits(user.email)}
                            disabled={updating}
                            className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700"
                          >
                            {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingUser(null)}
                            className="h-7 px-2"
                          >
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                          isOwnerUser
                            ? 'bg-emerald-100 text-emerald-700'
                            : user.usage_limit > 5
                            ? 'bg-blue-100 text-blue-700'
                            : user.usage_limit > 0
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {isOwnerUser ? (
                            <>
                              <Infinity className="w-3 h-3" />
                              Unlimited
                            </>
                          ) : (
                            <>
                              <Zap className="w-3 h-3" />
                              {user.usage_limit ?? 0}
                            </>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-zinc-500">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!isOwnerUser && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingUser(user.email);
                                setCreditAmount(10);
                                setAddMode(true);
                              }}
                              className="h-7 px-2 text-xs text-zinc-600 hover:text-blue-600"
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Credits
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMakeOwner(user.email)}
                              className="h-7 px-2 text-xs text-zinc-600 hover:text-emerald-600"
                            >
                              <Infinity className="w-3 h-3 mr-1" />
                              Unlimited
                            </Button>
                          </>
                        )}
                        {isOwnerUser && (
                          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                            <Shield className="w-3 h-3" />
                            Owner
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
          </TableBody>
        </Table>
      </TableScrollArea>

      <div className="text-sm text-zinc-500">
        Showing {filteredUsers.length} of {users.length} users
      </div>

      {/* Quick Actions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Quick Actions</h4>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• <strong>Add Credits:</strong> Adds the specified amount to user&apos;s existing balance</li>
          <li>• <strong>Set to:</strong> Replaces user&apos;s credits with the specified amount</li>
          <li>• <strong>Unlimited:</strong> Grants owner status with unlimited access</li>
        </ul>
      </div>
    </div>
  );
};

// User Activity Section (Admin Only) - beta window usage and procurement history,
// distinct from the Credits tab which manages usage_limit
const UserActivitySection = ({ isAdmin, stickyTop }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [procurementsByUser, setProcurementsByUser] = useState({});
  const [loadingProcurements, setLoadingProcurements] = useState(false);
  const [resettingUserId, setResettingUserId] = useState(null);
  const [sectionHeaderRef, sectionHeaderHeight] = useMeasuredHeight();
  // space-y-6 below adds a 24px gap between the section header and the table.
  const tableAreaOffset = stickyTop + sectionHeaderHeight + 24;

  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.getAdminUsers();
      setUsers(response.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      toastError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleToggleProcurements = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (!procurementsByUser[userId]) {
      setLoadingProcurements(true);
      try {
        const response = await api.getUserProcurements(userId);
        setProcurementsByUser(prev => ({ ...prev, [userId]: response.data.procurements || [] }));
      } catch (err) {
        toastError('Failed to fetch procurements');
      } finally {
        setLoadingProcurements(false);
      }
    }
  };

  const handleMakeOwner = async (email) => {
    if (!window.confirm(`Grant unlimited access to ${email}?`)) return;
    try {
      await api.makeUserOwner(email);
      toastSuccess(`${email} now has unlimited access`);
      fetchUsers();
    } catch (err) {
      toastError('Failed to update user');
    }
  };

  const handleResetBeta = async (userId, email) => {
    if (!window.confirm(`Reset beta window for ${email}? This gives them a fresh 30-day / 10-procurement allowance.`)) return;
    setResettingUserId(userId);
    try {
      await api.resetUserBeta(userId);
      toastSuccess(`Beta window reset for ${email}`);
      fetchUsers();
    } catch (err) {
      toastError('Failed to reset beta window');
    } finally {
      setResettingUserId(null);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-500">Login as admin to view user activity</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader top={stickyTop} headerRef={sectionHeaderRef}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900">User Activity</h3>
              <p className="text-sm text-zinc-500">Beta window usage and procurement history</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchUsers} className="border-zinc-200">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-50 border-zinc-200"
          />
        </div>
      </SectionHeader>

      <TableScrollArea offset={tableAreaOffset}>
        <Table containerClassName="relative w-full overflow-visible">
          <TableHeader>
            <TableRow>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Name / Email</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Role</TableHead>
              {/* Item 5c (2026-07-21): buyer persona - distinct from the
                  Owner/Beta access-tier "Role" column to the left. */}
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Persona</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 text-center">Procurements</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 text-center">Days Left</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500">Last Login</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 text-center">Status</TableHead>
              <TableHead className="sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-zinc-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.flatMap((user) => {
                  const isOwnerUser = user.is_owner || user.user_role === 'owner';
                  const daysRemaining = user.days_remaining ?? 30;
                  const isExpired = !isOwnerUser && daysRemaining <= 0;
                  const isExpanded = expandedUserId === user.user_id;

                  const rows = [
                    <TableRow key={user.user_id} className="hover:bg-zinc-50">
                      <TableCell>
                        <div className="font-medium text-zinc-900">{user.name || 'Unknown'}</div>
                        <div className="text-xs text-zinc-500 flex items-center gap-1">
                          <Mail className="w-3 h-3 text-zinc-400" />
                          {user.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                          isOwnerUser ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {isOwnerUser ? <Shield className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                          {isOwnerUser ? 'Owner' : 'Beta'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-zinc-700">
                        {user.role ? (
                          <span title={user.role === 'Other' && user.role_other_text ? user.role_other_text : undefined}>
                            {user.role}
                            {user.role === 'Other' && user.role_other_text ? ` (${user.role_other_text})` : ''}
                          </span>
                        ) : (
                          <span className="text-zinc-400 italic">Not set</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm text-zinc-700">
                        {isOwnerUser ? '—' : `${user.beta_procurement_count ?? 0}/10`}
                      </TableCell>
                      <TableCell className="text-center text-sm text-zinc-700">
                        {isOwnerUser ? '—' : daysRemaining}
                      </TableCell>
                      <TableCell className="text-xs text-zinc-500">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
                          isOwnerUser || !isExpired ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {isOwnerUser || !isExpired ? 'Active' : 'Expired'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleProcurements(user.user_id)}
                            className="h-7 px-2 text-xs text-zinc-600 hover:text-blue-600"
                          >
                            {isExpanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                            Procurements
                          </Button>
                          {!isOwnerUser && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMakeOwner(user.email)}
                                className="h-7 px-2 text-xs text-zinc-600 hover:text-emerald-600"
                              >
                                <Infinity className="w-3 h-3 mr-1" />
                                Make Owner
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetBeta(user.user_id, user.email)}
                                disabled={resettingUserId === user.user_id}
                                className="h-7 px-2 text-xs text-zinc-600 hover:text-amber-600"
                              >
                                {resettingUserId === user.user_id ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                )}
                                Reset Beta
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ];

                  if (isExpanded) {
                    rows.push(
                      <TableRow key={`${user.user_id}-procurements`}>
                        <TableCell colSpan={8} className="bg-zinc-50 p-4">
                          {loadingProcurements && !procurementsByUser[user.user_id] ? (
                            <div className="flex justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                            </div>
                          ) : (procurementsByUser[user.user_id] || []).length === 0 ? (
                            <p className="text-sm text-zinc-500 text-center py-2">No procurements yet</p>
                          ) : (
                            <div className="space-y-2">
                              {(procurementsByUser[user.user_id] || []).map((p) => (
                                <div key={p.serial_id} className="flex items-center justify-between text-sm bg-white border border-zinc-200 rounded p-2">
                                  <div>
                                    <span className="font-medium text-zinc-900">{p.serial_id}</span>
                                    <span className="text-zinc-500 ml-2">{p.user_requirement}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                                    <span className="px-2 py-0.5 bg-zinc-100 rounded-full">{getStatusLabel(p.status)}</span>
                                    <span>{p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }

                  return rows;
                })
              )}
          </TableBody>
        </Table>
      </TableScrollArea>

      <div className="text-sm text-zinc-500">
        Showing {filteredUsers.length} of {users.length} users
      </div>
    </div>
  );
};

// All Requests Section (Admin Only) - Item 3c, 2026-07-21. Flat table of
// every user's procurements with owner identity, no per-row admin actions
// and no daily-movement charts/funnels (that's a separate, not-yet-spec'd
// Batch 3 item) - just visibility.
// Item 2 (2026-07-22 pre-merge): click-to-sort columns for the All
// Requests table. Each accessor returns a directly-comparable value (string
// or number) so the sort itself stays a single generic comparator.
const ALL_REQUESTS_SORT_ACCESSORS = {
  serial_id: (r) => r.serial_id || '',
  title: (r) => r.title || '',
  owner_name: (r) => r.owner_name || '',
  status: (r) => r.status || '',
  updated_at: (r) => (r.updated_at ? new Date(r.updated_at).getTime() : 0),
};

const AllRequestsSortHeader = ({ label, sortKey, sortConfig, onSort, className = '', testId }) => {
  const isActive = sortConfig.key === sortKey;
  return (
    <TableHead
      className={`sticky top-0 z-10 bg-zinc-50 text-xs font-bold uppercase tracking-wider text-zinc-500 cursor-pointer select-none hover:text-zinc-700 ${className}`}
      onClick={() => onSort(sortKey)}
      data-testid={testId}
      aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className={`flex items-center gap-1 ${className.includes('text-center') ? 'justify-center' : ''}`}>
        {label}
        {isActive && (
          sortConfig.direction === 'asc'
            ? <ChevronUp className="w-3 h-3" data-testid={`${testId}-asc-indicator`} />
            : <ChevronDown className="w-3 h-3" data-testid={`${testId}-desc-indicator`} />
        )}
      </div>
    </TableHead>
  );
};

/**
 * MASTER-DATA REQUESTS (F1 / C1b-1b, B1) - the admin panel's list of
 * buyers blocked on missing master data.
 *
 * DERIVED, NEVER STORED. Every row comes from GET
 * /admin/master-data-requests, which computes them from the declarations
 * on the procurement documents themselves. A stored list would drift the
 * first time a writer was missed, and a withdrawn request would linger.
 * That means Refresh is the whole state model here: there is nothing to
 * invalidate.
 *
 * SECTIONED PER MASTER TYPE. Category is the only section today because it
 * is the only one with a producer; uom and location no-fit are the
 * unresolved-field contract's later slices and will arrive as their own
 * sections. The component renders whatever sections the server sends
 * rather than hardcoding one, so those slices are additive here.
 *
 * TWO SUBJECTS, AND THE PANEL MUST DISTINGUISH THEM. A LINE request means
 * one item on an RFP could not be classified; a HEADER request means the
 * whole procurement could not be. An administrator acts on them
 * differently, so the row says which and never shows a blank line number
 * as if it were a line.
 */
/**
 * THE THREE DISPOSITIONS (F1 / C1b-1b, B2b). Restated here as literals
 * because a shared constant is not available across the language boundary;
 * the Python declaration in routes/_master_data_request_dispositions.py is
 * authoritative and the server validates against it, so a drift here
 * surfaces as a 422 rather than as a silently-stored invention.
 *
 * NONE OF THEM RELEASES THE BUYER (Ashok ruling 7). Only the master gaining
 * the value, or the buyer reselecting, clears a park - so a dispositioned
 * request stays in this list, carrying its ruling, rather than disappearing
 * from it. Filtering it out would tell an administrator the problem was
 * over while the buyer is still stuck behind it.
 */
const REQUEST_DISPOSITIONS = [
  'Master updated',
  'Not Required - Duplicate Request',
  'Not Required by Policy',
];

/**
 * One row's ruling: either the standing disposition, or the control to
 * record one. IMMUTABLE - once a ruling exists this renders it and offers
 * no way to change it, matching the server's 409. DUPLICATE HANDLING IS
 * MANUAL: an administrator acts on one request and closes the other with
 * "Not Required - Duplicate Request"; nothing here infers which requests
 * are duplicates of each other.
 */
const RequestDispositionCell = ({ request, onRecorded }) => {
  const [choice, setChoice] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  if (request.disposition) {
    return (
      <div className="text-xs" data-testid={`disposition-${request.request_number}`}>
        <div className="font-medium text-zinc-800">{request.disposition.disposition}</div>
        {request.disposition.comment && (
          <div className="text-zinc-500 break-words">{request.disposition.comment}</div>
        )}
        <div className="text-zinc-400">
          {request.disposition.decided_by} · {formatDate(request.disposition.decided_at)}
        </div>
      </div>
    );
  }

  // THE COMMENT IS MANDATORY ON ALL THREE DISPOSITIONS (Ashok ruling,
  // 2026-08-26), and whitespace-only does not count - which is why this
  // trims before testing rather than checking `comment` itself. The server
  // enforces the same rule and refuses a blank one with a 422; this is
  // where an administrator finds out, before they have lost the ruling
  // they were part-way through recording.
  //
  // WHY ALL THREE. The two "Not Required" values leave no footprint
  // anywhere - an admin looked and declined, and the comment is the only
  // record of why. "Master updated" leaves a footprint with no link back:
  // the request carries the buyer's requested TEXT, never the code created
  // in response, so the comment ("701010 added") is the only place that
  // join exists.
  const trimmedComment = comment.trim();
  const canRecord = Boolean(choice) && Boolean(trimmedComment);

  const submit = async () => {
    if (!canRecord) return;
    setSaving(true);
    try {
      await api.recordMasterDataRequestDisposition(
        request.request_number, choice, trimmedComment
      );
      toastSuccess(`${request.request_number} recorded as "${choice}"`);
      await onRecorded();
    } catch (err) {
      // A 409 means a colleague ruled first; the message names what
      // already stands, so it is shown rather than replaced.
      toastError(err.response?.data?.detail || 'Failed to record the disposition');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5 min-w-[220px]" data-testid={`disposition-control-${request.request_number}`}>
      <select
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        className="w-full h-7 text-xs border border-zinc-200 rounded px-1 bg-white"
        data-testid={`disposition-select-${request.request_number}`}
        aria-label={`Disposition for ${request.request_number}`}
      >
        <option value="">Select a disposition...</option>
        {REQUEST_DISPOSITIONS.map((value) => (
          <option key={value} value={value}>{value}</option>
        ))}
      </select>
      <Input
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Comment (required)"
        className="h-7 text-xs"
        data-testid={`disposition-comment-${request.request_number}`}
      />
      <button
        type="button"
        onClick={submit}
        disabled={!canRecord || saving}
        className="text-xs font-medium text-white bg-[#0055FF] hover:bg-[#0044CC] disabled:bg-zinc-300 disabled:text-zinc-500 rounded px-2 py-1"
        data-testid={`disposition-submit-${request.request_number}`}
      >
        {saving ? 'Recording...' : 'Record'}
      </button>
    </div>
  );
};

const REQUEST_SUBJECT_LABELS = {
  header: 'Whole request',
  line: 'Line item',
};

const MasterDataRequestsSection = ({ isAdmin, stickyTop }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectionHeaderRef, sectionHeaderHeight] = useMeasuredHeight();
  const tableAreaOffset = stickyTop + sectionHeaderHeight + 24;

  const fetchRequests = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.getMasterDataRequests();
      setSections(response.data.sections || []);
    } catch (err) {
      console.error('Error fetching master data requests:', err);
      toastError('Failed to fetch master data requests');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const matches = (request) => {
    const needle = searchQuery.toLowerCase();
    if (!needle) return true;
    return [
      request.request_number,
      request.requested_text,
      request.serial_id,
      request.requestor,
      request.requestor_email,
    ].some((value) => String(value || '').toLowerCase().includes(needle));
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Inbox className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-500">Login as admin to view master data requests</p>
      </div>
    );
  }

  const totalRequests = sections.reduce((sum, s) => sum + (s.requests || []).length, 0);

  return (
    <div className="space-y-6">
      <SectionHeader top={stickyTop} headerRef={sectionHeaderRef}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Inbox className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900">Master Data Requests</h3>
              <p className="text-sm text-zinc-500">
                Buyers waiting on a master update before their request can proceed
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests} className="border-zinc-200">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by request number, requested category, serial, or requestor..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-50 border-zinc-200"
            data-testid="master-data-requests-search"
          />
        </div>
      </SectionHeader>

      <TableScrollArea offset={tableAreaOffset}>
        {loading ? (
          <p className="text-sm text-zinc-500 py-8 text-center">Loading requests...</p>
        ) : totalRequests === 0 ? (
          <div className="text-center py-12" data-testid="master-data-requests-empty">
            <Inbox className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-zinc-500">No outstanding master data requests</p>
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.master} className="mb-8" data-testid={`request-section-${section.master}`}>
              <h4 className="text-sm font-semibold text-zinc-700 mb-2">
                {section.label || section.master} ({(section.requests || []).length})
              </h4>
              <Table containerClassName="relative w-full overflow-visible">
                <TableHeader>
                  <TableRow>
                    <TableHead>Request</TableHead>
                    <TableHead>Requested</TableHead>
                    <TableHead>Procurement</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Requestor</TableHead>
                    <TableHead>Raised</TableHead>
                    <TableHead>Disposition</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(section.requests || []).filter(matches).map((request) => (
                    <TableRow key={request.request_number} data-testid={`request-row-${request.request_number}`}>
                      <TableCell className="font-mono text-xs">{request.request_number}</TableCell>
                      <TableCell className="max-w-sm break-words">{request.requested_text}</TableCell>
                      <TableCell className="font-mono text-xs">{request.serial_id}</TableCell>
                      <TableCell className="text-xs">
                        {/* A HEADER request has no line to point at, and a
                            blank cell where a line number belongs reads as
                            missing data rather than as a different kind of
                            request. */}
                        {REQUEST_SUBJECT_LABELS[request.subject] || request.subject}
                        {request.line_number !== null && request.line_number !== undefined
                          ? ` ${request.line_number}`
                          : ''}
                      </TableCell>
                      <TableCell className="text-xs">
                        {/* Resolved server-side through the one fallback
                            chain (name -> email -> a literal), so this
                            never renders blank and never renders "null" -
                            deliberately NOT AllRequestsSection's handling. */}
                        {request.requestor}
                      </TableCell>
                      <TableCell className="text-xs text-zinc-500">
                        {formatDate(request.declared_at)}
                      </TableCell>
                      <TableCell className="align-top">
                        <RequestDispositionCell request={request} onRecorded={fetchRequests} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))
        )}
      </TableScrollArea>
    </div>
  );
};


// THE ADMIN LIST IS PAGED (2026-09-01). It used to fetch with
// `.to_list(length=1000)` server-side - the same silent-truncation defect as
// the dashboard's `.to_list(100)` and worse, because this query has NO owner
// scoping to bound it. The cap is gone; this pages instead, so every row is
// reachable and the total is stated. SEARCH AND COLUMN SORT REMAIN
// CLIENT-SIDE OVER THE CURRENT PAGE, which is what they have always been -
// they sorted whatever the one fetch returned. That is unchanged in kind,
// not newly narrowed, but it is worth knowing when reading a sorted column.
const ADMIN_PAGE_SIZE = 100;

const AllRequestsSection = ({ isAdmin, stickyTop }) => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Default sort: Updated desc (Item 2) - most recently active requests first.
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });
  const [sectionHeaderRef, sectionHeaderHeight] = useMeasuredHeight();
  const tableAreaOffset = stickyTop + sectionHeaderHeight + 24;

  const handleSort = (key) => {
    setSortConfig((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  };

  const fetchRequests = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.getAllProcurementsAdmin({ page, pageSize: ADMIN_PAGE_SIZE });
      setRequests(response.data.procurements || []);
      setTotal(response.data.total ?? (response.data.procurements || []).length);
    } catch (err) {
      console.error('Error fetching all requests:', err);
      toastError('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filteredRequests = requests.filter((r) =>
    r.serial_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.owner_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.owner_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedRequests = [...filteredRequests].sort((a, b) => {
    const accessor = ALL_REQUESTS_SORT_ACCESSORS[sortConfig.key];
    const aVal = accessor(a);
    const bVal = accessor(b);
    const cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
    return sortConfig.direction === 'asc' ? cmp : -cmp;
  });

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <ClipboardList className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-500">Login as admin to view all requests</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader top={stickyTop} headerRef={sectionHeaderRef}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardList className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900">All Requests</h3>
              <p className="text-sm text-zinc-500">Every procurement request across all users</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests} className="border-zinc-200">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Search by serial, title, owner name, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-zinc-50 border-zinc-200"
          />
        </div>
      </SectionHeader>

      <TableScrollArea offset={tableAreaOffset}>
        <Table containerClassName="relative w-full overflow-visible">
          <TableHeader>
            <TableRow>
              <AllRequestsSortHeader label="Serial" sortKey="serial_id" sortConfig={sortConfig} onSort={handleSort} testId="sort-header-serial" />
              <AllRequestsSortHeader label="Title" sortKey="title" sortConfig={sortConfig} onSort={handleSort} testId="sort-header-title" />
              <AllRequestsSortHeader label="Owner" sortKey="owner_name" sortConfig={sortConfig} onSort={handleSort} testId="sort-header-owner" />
              <AllRequestsSortHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} className="text-center" testId="sort-header-status" />
              <AllRequestsSortHeader label="Updated" sortKey="updated_at" sortConfig={sortConfig} onSort={handleSort} testId="sort-header-updated" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
                </TableCell>
              </TableRow>
            ) : sortedRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                  No requests found
                </TableCell>
              </TableRow>
            ) : (
              sortedRequests.map((r) => (
                <TableRow key={r.id} className="hover:bg-zinc-50">
                  <TableCell className="font-mono text-sm text-[#0055FF]">{r.serial_id || '—'}</TableCell>
                  <TableCell className="text-sm text-zinc-900 max-w-xs truncate" title={r.title}>{r.title || '—'}</TableCell>
                  <TableCell>
                    <div className="font-medium text-zinc-900 text-sm">{r.owner_name || 'Unknown'}</div>
                    {r.owner_email && (
                      <div className="text-xs text-zinc-500 flex items-center gap-1">
                        <Mail className="w-3 h-3 text-zinc-400" />
                        {r.owner_email}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-zinc-100 text-zinc-600">
                      {getStatusLabel(r.status)}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500">
                    {r.updated_at ? formatDate(r.updated_at) : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableScrollArea>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-zinc-500" data-testid="admin-requests-range">
          Showing {filteredRequests.length} of {requests.length} on this page &middot; {total} in total
        </div>
        {total > ADMIN_PAGE_SIZE && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="admin-page-prev">Previous</Button>
            <span className="text-xs text-zinc-500" data-testid="admin-page-indicator">
              Page {page} of {Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE))}
            </span>
            <Button size="sm" variant="outline"
                    disabled={page >= Math.ceil(total / ADMIN_PAGE_SIZE)}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="admin-page-next">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Learning System Stats Section (Admin Only)
const LearningStatsSection = ({ isAdmin, stickyTop }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aggregating, setAggregating] = useState(false);
  const [signals, setSignals] = useState([]);
  const [signalsLoading, setSignalsLoading] = useState(true);
  const [deletingSignalId, setDeletingSignalId] = useState(null);
  const [resettingAll, setResettingAll] = useState(false);
  const [resettingCategory, setResettingCategory] = useState(false);
  const [selectedResetCategory, setSelectedResetCategory] = useState('');

  const fetchStats = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const response = await api.getLearningStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching learning stats:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchSignals = useCallback(async () => {
    if (!isAdmin) return;
    setSignalsLoading(true);
    try {
      const response = await api.getLearningSignals();
      setSignals(response.data.signals || []);
    } catch (err) {
      console.error('Error fetching learning signals:', err);
    } finally {
      setSignalsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchStats();
    fetchSignals();
  }, [fetchStats, fetchSignals]);

  const handleAggregate = async () => {
    setAggregating(true);
    try {
      await api.aggregateLearningSignals();
      toastSuccess('Signals aggregated successfully');
      fetchStats();
      fetchSignals();
    } catch (err) {
      toastError('Failed to aggregate signals');
    } finally {
      setAggregating(false);
    }
  };

  const handleDeleteSignal = async (signalId) => {
    setDeletingSignalId(signalId);
    try {
      await api.deleteLearningSignal(signalId);
      toastSuccess('Signal deleted');
      fetchSignals();
      fetchStats();
    } catch (err) {
      toastError('Failed to delete signal');
    } finally {
      setDeletingSignalId(null);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset ALL accumulated learning data? This deletes every edit signal, template signal, and captured edit across all categories. This cannot be undone.')) return;
    setResettingAll(true);
    try {
      const response = await api.resetLearning();
      toastSuccess(`Reset complete - ${response.data.total_deleted} document(s) deleted`);
      fetchSignals();
      fetchStats();
    } catch (err) {
      toastError('Failed to reset learning data');
    } finally {
      setResettingAll(false);
    }
  };

  const handleResetCategory = async () => {
    if (!selectedResetCategory) {
      toastError('Select a category first');
      return;
    }
    if (!window.confirm(`Reset all learning data for category "${selectedResetCategory}"? This cannot be undone.`)) return;
    setResettingCategory(true);
    try {
      const response = await api.resetLearning(selectedResetCategory);
      toastSuccess(`Reset complete for ${selectedResetCategory} - ${response.data.total_deleted} document(s) deleted`);
      setSelectedResetCategory('');
      fetchSignals();
      fetchStats();
    } catch (err) {
      toastError('Failed to reset learning data');
    } finally {
      setResettingCategory(false);
    }
  };

  const availableCategories = [...new Set(signals.map(s => s.category))].filter(Boolean);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Brain className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-500">Login as admin to view learning system stats</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#0055FF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <SectionHeader top={stickyTop}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Brain className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-zinc-900">Learning System</h3>
              <p className="text-sm text-zinc-500">AI learns from user edits to improve RFP generation</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchStats}
              className="border-zinc-200"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleAggregate}
              disabled={aggregating}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {aggregating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Run Aggregation
            </Button>
          </div>
        </div>
      </SectionHeader>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-zinc-900">{stats?.total_edits_captured || 0}</div>
          <div className="text-xs text-zinc-500 mt-1">Total Edits Captured</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">{stats?.active_edit_signals || 0}</div>
          <div className="text-xs text-zinc-500 mt-1">Active Edit Signals</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">{stats?.active_template_signals || 0}</div>
          <div className="text-xs text-zinc-500 mt-1">Template Signals</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-emerald-600">{stats?.active_learned_questions || 0}</div>
          <div className="text-xs text-zinc-500 mt-1">Learned Questions</div>
        </div>
      </div>

      {/* Top Categories */}
      {stats?.top_categories?.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-zinc-700 mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-zinc-400" />
            Top Categories by Edits
          </h4>
          <div className="space-y-2">
            {stats.top_categories.map((cat, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-zinc-600">{cat.category}</span>
                <span className="text-sm font-medium text-zinc-900">{cat.edits} edits</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Most Edited Fields */}
      {stats?.most_edited_fields?.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-zinc-700 mb-3">Most Edited Fields</h4>
          <div className="flex flex-wrap gap-2">
            {stats.most_edited_fields.map((field, idx) => (
              <span 
                key={idx} 
                className="px-3 py-1 bg-zinc-100 text-zinc-700 text-xs rounded-full"
              >
                {field.field.replace(/_/g, ' ')} ({field.edits})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Accumulated Signals Table */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
          <h4 className="text-sm font-medium text-zinc-700">Accumulated Signals ({signals.length})</h4>
          <Button variant="outline" size="sm" onClick={fetchSignals} className="border-zinc-200">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        {signalsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
          </div>
        ) : signals.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8">No signals accumulated yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-zinc-500 border-b border-zinc-100 bg-zinc-50">
                  <th className="px-4 py-2 font-medium">Category</th>
                  <th className="px-4 py-2 font-medium">Field</th>
                  <th className="px-4 py-2 font-medium">Count</th>
                  <th className="px-4 py-2 font-medium">Confidence</th>
                  <th className="px-4 py-2 font-medium">Last Updated</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {signals.map((signal) => (
                  <tr key={signal.id} className="border-b border-zinc-50 last:border-0">
                    <td className="px-4 py-2 text-zinc-700">{signal.category}</td>
                    <td className="px-4 py-2 text-zinc-600">{(signal.field_name || '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-2 text-zinc-600">{signal.frequency ?? '-'}</td>
                    <td className="px-4 py-2 text-zinc-600">{signal.confidence !== undefined ? `${Math.round(signal.confidence * 100)}%` : '-'}</td>
                    <td className="px-4 py-2 text-zinc-500 text-xs">
                      {signal.last_updated ? new Date(signal.last_updated).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSignal(signal.id)}
                        disabled={deletingSignalId === signal.id}
                        className="text-zinc-400 hover:text-red-600"
                        data-testid={`delete-signal-${signal.id}`}
                      >
                        {deletingSignalId === signal.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reset Controls */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-4">
        <h4 className="text-sm font-medium text-zinc-700">Reset Learning Data</h4>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetAll}
            disabled={resettingAll}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="reset-all-learning-btn"
          >
            {resettingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Reset All Learning
          </Button>
          <div className="flex items-center gap-2">
            <select
              value={selectedResetCategory}
              onChange={(e) => setSelectedResetCategory(e.target.value)}
              className="text-sm border border-zinc-200 rounded px-2 py-1.5"
              data-testid="reset-category-select"
            >
              <option value="">Select category...</option>
              {availableCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetCategory}
              disabled={resettingCategory || !selectedResetCategory}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="reset-category-btn"
            >
              {resettingCategory ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Reset for Category
            </Button>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-xs text-blue-800">
          Learning is currently category-shared across all users. Signals appear once used at least 2 times, applied at 3 times.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-purple-900 mb-2">How Learning Works</h4>
        <ol className="text-xs text-purple-700 space-y-1 list-decimal list-inside">
          <li>Users edit generated RFPs → Edits are captured in <code className="bg-purple-100 px-1 rounded">rfp_field_edits</code></li>
          <li>Aggregation job processes edits → Creates signals in <code className="bg-purple-100 px-1 rounded">edit_signals</code></li>
          <li>Next RFP generation → Signals injected into AI prompt for category</li>
          <li>AI generates smarter RFPs → Fewer user edits needed over time</li>
        </ol>
      </div>
    </div>
  );
};

// Main Component
const MasterDataManagement = () => {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState('locations');

  // Measured height of the two-row sticky page header, so section headers
  // below it can pin flush with zero pre-pin travel (top: headerHeight).
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const updateHeight = () => setHeaderHeight(el.offsetHeight);
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Data states
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [loadingUoms, setLoadingUoms] = useState(true);
  // Scope Elements column truncation (same treatment as risk domain
  // assessments - truncated by default, expand-on-click per row).
  const [expandedScopeElements, setExpandedScopeElements] = useState(() => new Set());
  const toggleScopeElements = (id) => {
    setExpandedScopeElements(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Check admin status on mount
  useEffect(() => {
    const checkAdmin = async () => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          const response = await api.verifyAdmin();
          setIsAdmin(response.data.is_admin);
        } catch (err) {
          console.debug('Admin verification failed:', err.message);
          localStorage.removeItem('admin_token');
          setIsAdmin(false);
        }
      }
    };
    checkAdmin();
  }, []);

  // Fetch data
  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const response = await api.getMasterLocations();
      setLocations(response.data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await api.getMasterCategories();
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    setLoadingCurrencies(true);
    try {
      const response = await api.getMasterCurrencies();
      setCurrencies(response.data);
    } catch (err) {
      console.error('Error fetching currencies:', err);
    } finally {
      setLoadingCurrencies(false);
    }
  }, []);

  const fetchUoms = useCallback(async () => {
    setLoadingUoms(true);
    try {
      const response = await api.getMasterUoms();
      setUoms(response.data);
    } catch (err) {
      console.error('Error fetching UoMs:', err);
    } finally {
      setLoadingUoms(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchCategories();
    fetchCurrencies();
    fetchUoms();
  }, [fetchLocations, fetchCategories, fetchCurrencies, fetchUoms]);

  const handleLogout = async () => {
    try {
      await api.adminLogout();
    } catch (err) {
      console.debug('Logout API error (ignoring):', err.message);
    }
    localStorage.removeItem('admin_token');
    setIsAdmin(false);
    toastSuccess('Logged out successfully');
  };

  // F14: the confirm text states what ACTUALLY happens. The word "delete"
  // appears nowhere in this flow, because nothing is deleted - the row is
  // retained, existing procurements referencing it keep resolving, and it
  // can be reactivated from this same screen.
  const deactivateMessage = (noun) =>
    `Deactivate this ${noun}?\n\n` +
    `It will be kept, not removed, and hidden from selection in the wizard.\n` +
    `Existing procurements that reference it will continue to resolve it.\n` +
    `You can reactivate it from this screen at any time.`;

  const handleDeactivateLocation = async (id) => {
    if (!window.confirm(deactivateMessage('location'))) return;
    try {
      await api.deleteMasterLocation(id);
      toastSuccess('Location deactivated');
      fetchLocations();
    } catch (err) {
      toastError('Failed to deactivate location');
    }
  };

  const handleActivateLocation = async (id) => {
    try {
      await api.activateMasterLocation(id);
      toastSuccess('Location activated');
      fetchLocations();
    } catch (err) {
      toastError('Failed to activate location');
    }
  };

  const handleDeactivateCategory = async (id) => {
    if (!window.confirm(deactivateMessage('category'))) return;
    try {
      await api.deleteMasterCategory(id);
      toastSuccess('Category deactivated');
      fetchCategories();
    } catch (err) {
      toastError('Failed to deactivate category');
    }
  };

  const handleActivateCategory = async (id) => {
    try {
      await api.activateMasterCategory(id);
      toastSuccess('Category activated');
      fetchCategories();
    } catch (err) {
      toastError('Failed to activate category');
    }
  };

  const handleDeactivateUom = async (id) => {
    if (!window.confirm(deactivateMessage('UoM'))) return;
    try {
      await api.deleteMasterUom(id);
      toastSuccess('UoM deactivated');
      fetchUoms();
    } catch (err) {
      toastError('Failed to deactivate UoM');
    }
  };

  const handleActivateUom = async (id) => {
    try {
      await api.activateMasterUom(id);
      toastSuccess('UoM activated');
      fetchUoms();
    } catch (err) {
      toastError('Failed to activate UoM');
    }
  };

  // Column definitions
  const locationColumns = deriveMasterColumns(locations, [
    { key: 'location_code', label: 'Code' },
    { key: 'location_name', label: 'Name' },
    { key: 'location_address', label: 'Address' }
  ]);

  // VERIFICATION FIX (post-596050b): a column whose key the backend
  // strips for non-admin callers must not render at all for them - not
  // even as an always-present "—" placeholder column. The column is
  // data-presence-driven: it renders only when at least one row in the
  // CURRENT payload actually carries the key (checked via
  // hasOwnProperty, not truthiness - an admin row with an empty-but-
  // present scope_elements array still counts as "carries the field").
  // The backend's stripping stays the single decision point; this is
  // never a client-side isAdmin check. Applies to categoryColumns'
  // scope_elements and uomColumns' measurement_family/unit_semantics
  // below - none of these existed with this behavior before this fix.
  const columnIfDataPresent = (data, columnDef) =>
    data.some((row) => Object.prototype.hasOwnProperty.call(row, columnDef.key)) ? [columnDef] : [];

  const categoryColumns = deriveMasterColumns(categories, [
    { key: 'custom_code', label: 'Code' },
    { key: 'l1_category', label: 'L1 Category' },
    { key: 'l2_subcategory', label: 'L2 Subcategory' },
    { key: 'l3_description', label: 'Description' },
    { key: 'category_name', label: 'Name' },
    {
      key: 'flag',
      label: 'Type',
      render: (val) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          val === 'Goods' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
        }`}>
          {val}
        </span>
      )
    },
    { key: 'unspsc_code', label: 'UNSPSC' },
    ...columnIfDataPresent(categories, {
      key: 'scope_elements',
      label: 'Scope Elements',
      render: (val, item) => {
        const elements = Array.isArray(val) ? val : [];
        if (elements.length === 0) {
          return <span className="text-zinc-400 italic">—</span>;
        }
        const joined = elements.join(', ');
        const isExpanded = expandedScopeElements.has(item.id);
        const isTruncatable = joined.length > 60;
        const displayText = isExpanded || !isTruncatable ? joined : `${joined.slice(0, 60)}...`;
        return (
          <div className={isExpanded ? 'max-w-md whitespace-normal' : 'max-w-xs truncate'} title={!isExpanded ? joined : undefined}>
            <span>{displayText}</span>
            {isTruncatable && (
              <button
                type="button"
                onClick={() => toggleScopeElements(item.id)}
                className="ml-1.5 text-[#0055FF] text-xs font-medium hover:underline"
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        );
      }
    }),
    // B3 (2026-07-30): admin-only, server-side only (no wizard/client
    // logic reads this field - confirmed, see the ticket's RULE 0
    // trace) - same columnIfDataPresent masking as Scope Elements above.
    // null (not authored) renders the same em-dash empty-state as an
    // authored-but-empty row would - this column never implies "no
    // domains apply" either way; resolution's DEFAULT fallback (server
    // side) is what actually governs an unauthored category.
    ...columnIfDataPresent(categories, {
      key: 'applicable_risk_domains',
      label: 'Applicable Risk Domains',
      render: (val) => {
        const domains = Array.isArray(val) ? val : [];
        if (domains.length === 0) {
          return <span className="text-zinc-400 italic">—</span>;
        }
        return (
          <span className="text-xs">
            {domains.map((d) => d.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())).join(', ')}
          </span>
        );
      }
    }),
    // LOCATION RESOLUTION / CARDINALITY (Ashok ruling, 2026-09-08): the
    // two columns that replaced Default Location Mode / Location Aware in
    // place. Admin-only on the server (category_master_contract.
    // ADMIN_ONLY_FIELDS), so the same data-presence masking as the two
    // columns above - never a client-side role check. Rendered as the
    // stored token, verbatim: "Not Applicable" in Cardinality is a VALUE ("there is
    // no site to count"), not an empty state, so it must never fall
    // through to the em-dash placeholder. No consumer reads either field
    // here; this is the admin seeing every stored field.
    ...columnIfDataPresent(categories, {
      key: 'location_resolution',
      label: 'Location Resolution',
      render: (val) => <span className="text-xs font-mono">{val}</span>
    }),
    ...columnIfDataPresent(categories, {
      key: 'cardinality',
      label: 'Cardinality',
      render: (val) => <span className="text-xs font-mono">{val}</span>
    })
  ]);

  const currencyColumns = deriveMasterColumns(currencies, [
    { key: 'currency_code', label: 'Code' },
    { key: 'currency_name', label: 'Currency Name' }
  ]);

  // POST-LAUNCH (role-scoped master data visibility): measurement_family/
  // unit_semantics are admin-only - the backend (GET /master/uoms) already
  // strips both keys entirely for non-admin callers browsing this table
  // (never for the wizard's own validation-purpose fetch, which always
  // gets them - see api.getMasterUomsForValidation). columnIfDataPresent
  // above means neither column renders at all when the payload lacks the
  // key - never gated on an isAdmin check here, the backend's stripping
  // is the single decision point.
  const uomColumns = deriveMasterColumns(uoms, [
    { key: 'uom_code', label: 'Code' },
    { key: 'uom_name', label: 'Name' },
    {
      key: 'applies_to',
      label: 'Applies To',
      render: (val) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          val === 'goods' ? 'bg-blue-100 text-blue-700' : val === 'services' ? 'bg-purple-100 text-purple-700' : 'bg-zinc-100 text-zinc-600'
        }`}>
          {val}
        </span>
      )
    },
    ...columnIfDataPresent(uoms, {
      key: 'measurement_family',
      label: 'Measurement Family',
      render: (val) => val ? val : <span className="text-zinc-400 italic">—</span>
    }),
    ...columnIfDataPresent(uoms, {
      key: 'unit_semantics',
      label: 'Unit Semantics',
      render: (val) => val ? val : <span className="text-zinc-400 italic">—</span>
    }),
    // QUANTITY TYPE - a PLAIN column, deliberately NOT columnIfDataPresent
    // (Ashok ruling, 2026-08-24: an admin can see every stored field).
    // The two masked columns above are masked because GET /master/uoms
    // pop()s them for non-admin browsers; quantity_type is NOT in that
    // pop list, so every caller receives it and there is nothing to mask.
    // Wrapping it in columnIfDataPresent would imply a stripping that
    // does not happen and would hide the column outright the moment the
    // master is empty.
    {
      key: 'quantity_type',
      label: 'Quantity Type',
      render: (val) => val ? val : <span className="text-zinc-400 italic">—</span>
    }
  ]);

  const visibleNavItems = NAV_ITEMS.filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-white">
      {/* Header - two rows, both pinned together flush at the top (zero pre-pin travel) */}
      <header ref={headerRef} className={TOP_BAR_STICKY}>
        {/* Row 1: ProcMatrix wordmark only - matches Layout's logo bar height/alignment */}
        <div className="h-14 flex items-center justify-end px-6 border-b border-zinc-200">
          <img src="/brand/logo-wordmark.svg" alt="ProcMatrix" className="h-8 w-auto" />
        </div>

        {/* Row 2: Back to Dashboard | title ... view-only/admin chip ... Admin Login */}
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-zinc-500 hover:text-zinc-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-zinc-200" />
              <h1 className="text-xl font-bold text-zinc-900">Master Data Management</h1>
            </div>

            <div className="flex items-center gap-3">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium cursor-default ${
                        isAdmin ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      <Shield className="w-3.5 h-3.5" />
                      {isAdmin ? 'Admin' : 'View only'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isAdmin
                      ? 'You are logged in as admin and can upload and manage master data.'
                      : 'View Only Mode: Login as admin to upload and manage master data.'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {isAdmin ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="border-zinc-200"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLoginModal(true)}
                  className="border-zinc-200"
                  data-testid="admin-login-trigger"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Admin Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop sidebar - sticky, flush below the page header, like the main app sidebar */}
        <aside className="hidden lg:block w-56 shrink-0 bg-zinc-50 border-r border-zinc-200">
          <nav
            className="sticky overflow-y-auto p-4 space-y-1"
            style={{ top: headerHeight, maxHeight: `calc(100vh - ${headerHeight}px)` }}
          >
            {visibleNavItems.map((item) => (
              <button
                key={item.value}
                onClick={() => setActiveTab(item.value)}
                data-testid={`admin-nav-${item.value}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium w-full text-left transition-all duration-200 ${
                  activeTab === item.value
                    ? 'bg-[#0055FF] text-white'
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                }`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content column */}
        <div className="flex-1 min-w-0">
          {/* Mobile nav strip - horizontal scroll, replaces the sidebar below lg */}
          <div className="lg:hidden overflow-x-auto bg-zinc-50 border-b border-zinc-200">
            <div className="flex gap-2 px-4 py-2 whitespace-nowrap">
              {visibleNavItems.map((item) => (
                <button
                  key={item.value}
                  onClick={() => setActiveTab(item.value)}
                  data-testid={`admin-nav-mobile-${item.value}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium shrink-0 transition-colors ${
                    activeTab === item.value
                      ? 'bg-[#0055FF] text-white'
                      : 'bg-white text-zinc-600 border border-zinc-200'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="px-6 pb-8">
              <TabsContent value="locations" className="mt-0">
                <MasterDataTable
                  title="Location Master"
                  description="Ship-to locations for procurement deliveries"
                  icon={MapPin}
                  data={locations}
                  columns={locationColumns}
                  isAdmin={isAdmin}
                  onUpload={api.uploadMasterLocations}
                  onDelete={handleDeactivateLocation}
                  onActivate={handleActivateLocation}
                  onRefresh={fetchLocations}
                  loading={loadingLocations}
                  stickyTop={headerHeight}
                />
              </TabsContent>

              <TabsContent value="categories" className="mt-0">
                <MasterDataTable
                  title="Category Master"
                  description="Procurement categories with UNSPSC codes"
                  icon={FolderTree}
                  data={categories}
                  columns={categoryColumns}
                  isAdmin={isAdmin}
                  onUpload={api.uploadMasterCategories}
                  onDelete={handleDeactivateCategory}
                  onActivate={handleActivateCategory}
                  onRefresh={fetchCategories}
                  loading={loadingCategories}
                  stickyTop={headerHeight}
                />
              </TabsContent>

              <TabsContent value="uoms" className="mt-0">
                <MasterDataTable
                  title="UoM Master"
                  description="Controlled unit-of-measure vocabulary for line items"
                  icon={Ruler}
                  data={uoms}
                  columns={uomColumns}
                  isAdmin={isAdmin}
                  onUpload={api.uploadMasterUoms}
                  onDelete={handleDeactivateUom}
                  onActivate={handleActivateUom}
                  onRefresh={fetchUoms}
                  loading={loadingUoms}
                  stickyTop={headerHeight}
                />
              </TabsContent>

              <TabsContent value="currencies" className="mt-0">
                <MasterDataTable
                  title="Currency Master"
                  description="Supported currencies for RFP pricing"
                  icon={DollarSign}
                  data={currencies}
                  columns={currencyColumns}
                  isAdmin={isAdmin}
                  onUpload={api.uploadMasterCurrencies}
                  onDelete={() => toastInfo('Currency deletion not supported')}
                  onRefresh={fetchCurrencies}
                  loading={loadingCurrencies}
                  stickyTop={headerHeight}
                />
              </TabsContent>

              <TabsContent value="exchange-rates" className="mt-0">
                <ExchangeRatesSection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>

              <TabsContent value="policy" className="mt-0 space-y-8">
                <PolicySection
                  isAdmin={isAdmin}
                  onRefresh={() => {}}
                  policyType="procurement"
                  title="Procurement Policy"
                  description="Current active procurement policy document"
                  stickyTop={headerHeight}
                />
                <PolicySection
                  isAdmin={isAdmin}
                  onRefresh={() => {}}
                  policyType="vendor_risk"
                  title="Vendor Risk Assessment Policy"
                  description="Current active vendor risk assessment policy document"
                  stickyTop={headerHeight}
                />
              </TabsContent>

              <TabsContent value="risk" className="mt-0">
                <RiskFrameworkSection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>

              <TabsContent value="credits" className="mt-0">
                <UserCreditsSection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>

              <TabsContent value="invites" className="mt-0">
                <InvitesSection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>

              <TabsContent value="learning" className="mt-0">
                <LearningStatsSection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>

              <TabsContent value="user-activity" className="mt-0">
                <UserActivitySection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>

              <TabsContent value="all-requests" className="mt-0">
                <AllRequestsSection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>

              <TabsContent value="master-data-requests" className="mt-0">
                <MasterDataRequestsSection isAdmin={isAdmin} stickyTop={headerHeight} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      {/* Admin Login Modal */}
      <AdminLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={setIsAdmin}
      />
    </div>
  );
};

// Named export for testing (Item 2, 2026-07-22 pre-merge) - the section is
// otherwise only reachable nested inside the full admin page's tabs.
export { AllRequestsSection, MasterDataRequestsSection };
export default MasterDataManagement;
